import React, { useEffect, useMemo, useState } from 'react';
import { showcaseAPI } from '@/lib/api';

// ============================================================
// Vitrine publique (boutique + portfolio) — 3 templates
// Page 100% autonome (pas d'AppLayout), thème via variables CSS.
// ============================================================

const THEME_CSS = `
.vitrine-root { --v-radius: 18px; font-family: 'Inter', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; min-height:100vh; background: var(--v-bg); color: var(--v-fg); }
.vitrine-root * { box-sizing: border-box; }
.vitrine-root a { color: inherit; text-decoration: none; }
.v-wrap { max-width: 1080px; margin: 0 auto; padding: 0 20px; }
.v-section { padding: 64px 0; }
.v-section-title { font-size: 28px; font-weight: 800; margin: 0 0 6px; letter-spacing: -0.02em; }
.v-section-sub { color: var(--v-muted); margin: 0 0 28px; }
.v-grid { display: grid; gap: 18px; }
.v-grid-2 { grid-template-columns: repeat(auto-fill, minmax(300px,1fr)); }
.v-grid-3 { grid-template-columns: repeat(auto-fill, minmax(240px,1fr)); }
.v-card { background: var(--v-card); border: 1px solid var(--v-border); border-radius: var(--v-radius); padding: 20px; box-shadow: var(--v-shadow); transition: transform .18s ease, box-shadow .18s ease; }
.v-card:hover { transform: translateY(-3px); box-shadow: var(--v-shadow-hover); }
.v-badge { display:inline-block; font-size: 12px; padding: 3px 10px; border-radius: 999px; background: var(--v-accent-soft); color: var(--v-accent-strong); font-weight:600; }
.v-tag { font-size: 11px; padding: 2px 8px; border-radius: 6px; background: var(--v-chip); color: var(--v-muted); }
.v-btn { display:inline-flex; align-items:center; gap:8px; font-weight:700; font-size:14px; padding: 11px 20px; border-radius: 12px; cursor:pointer; border:1px solid transparent; transition: filter .15s, transform .15s; }
.v-btn:hover { filter: brightness(1.06); transform: translateY(-1px); }
.v-btn-primary { background: var(--v-accent); color: var(--v-on-accent); }
.v-btn-ghost { background: transparent; border-color: var(--v-border); color: var(--v-fg); }
.v-hero { position: relative; padding: 90px 0 70px; overflow: hidden; }
.v-hero-bg { position:absolute; inset:0; background-size:cover; background-position:center; opacity: var(--v-hero-bg-opacity); }
.v-hero-inner { position: relative; display:flex; gap: 28px; align-items:center; flex-wrap: wrap; }
.v-avatar { width: 116px; height: 116px; border-radius: 50%; object-fit: cover; border: 4px solid var(--v-accent-soft); flex-shrink:0; }
.v-hero h1 { font-size: 42px; line-height: 1.05; margin: 0 0 10px; font-weight: 900; letter-spacing:-0.03em; }
.v-hero .v-sub { font-size: 18px; color: var(--v-muted); max-width: 640px; margin: 0 0 8px; }
.v-price { font-weight: 800; color: var(--v-accent-strong); }
.v-stars { color: #f59e0b; letter-spacing: 2px; }
.v-timeline .v-tl-item { display:flex; gap:16px; padding-bottom: 22px; position:relative; }
.v-timeline .v-tl-dot { width:12px; height:12px; border-radius:50%; background: var(--v-accent); margin-top:6px; flex-shrink:0; box-shadow: 0 0 0 4px var(--v-accent-soft); }
.v-timeline .v-tl-line { position:absolute; left:5px; top:18px; bottom:-4px; width:2px; background: var(--v-border); }
.v-input { width:100%; padding: 11px 13px; border-radius: 11px; border:1px solid var(--v-border); background: var(--v-card); color: var(--v-fg); font-size: 14px; }
.v-input:focus { outline: 2px solid var(--v-accent-soft); border-color: var(--v-accent); }
.v-footer { border-top:1px solid var(--v-border); padding: 34px 0; color: var(--v-muted); font-size: 14px; }
.v-socials { display:flex; gap: 14px; flex-wrap:wrap; }
.v-nav { position: sticky; top:0; z-index: 20; backdrop-filter: blur(10px); background: var(--v-nav-bg); border-bottom:1px solid var(--v-border); }
.v-nav-inner { display:flex; align-items:center; justify-content:space-between; padding: 14px 20px; max-width:1080px; margin:0 auto; }
.v-nav a.link { color: var(--v-muted); font-weight:600; font-size:14px; margin-left:18px; }
.v-nav a.link:hover { color: var(--v-fg); }
@media (max-width:640px){ .v-hero h1{font-size:32px} .v-nav .links{display:none} }

/* -------- Template: MODERN -------- */
.tpl-modern { --v-bg:#f6f9fc; --v-fg:#0f172a; --v-muted:#64748b; --v-card:#ffffff; --v-border:#e2e8f0; --v-chip:#f1f5f9;
  --v-accent:#0ea5b7; --v-accent-strong:#0e7490; --v-accent-soft:#cffafe; --v-on-accent:#ffffff;
  --v-shadow:0 1px 3px rgba(15,23,42,.06); --v-shadow-hover:0 12px 30px rgba(14,165,183,.16);
  --v-hero-bg-opacity:0.12; --v-nav-bg:rgba(246,249,252,.8); }

/* -------- Template: DARK TECH -------- */
.tpl-dark_tech { --v-bg:#0b1020; --v-fg:#e5e9f5; --v-muted:#8b93b5; --v-card:#121a33; --v-border:#233056; --v-chip:#1b2444;
  --v-accent:#22d3ee; --v-accent-strong:#67e8f9; --v-accent-soft:rgba(34,211,238,.14); --v-on-accent:#04121a;
  --v-shadow:0 0 0 1px rgba(34,211,238,.05); --v-shadow-hover:0 10px 40px rgba(34,211,238,.22);
  --v-hero-bg-opacity:0.25; --v-nav-bg:rgba(11,16,32,.75); }
.tpl-dark_tech .v-hero h1 { background: linear-gradient(90deg,#22d3ee,#a855f7); -webkit-background-clip:text; background-clip:text; color:transparent; }

/* -------- Template: MINIMAL -------- */
.tpl-minimal { --v-bg:#ffffff; --v-fg:#111111; --v-muted:#777777; --v-card:#ffffff; --v-border:#eaeaea; --v-chip:#f5f5f5;
  --v-accent:#111111; --v-accent-strong:#000000; --v-accent-soft:#f0f0f0; --v-on-accent:#ffffff;
  --v-shadow:none; --v-shadow-hover:none; --v-radius:8px; --v-hero-bg-opacity:0.06; --v-nav-bg:rgba(255,255,255,.85); }
.tpl-minimal .v-card:hover { transform:none; border-color:#111; }
`;

function Stars({ n = 5 }) {
  return <span className="v-stars">{'★'.repeat(Math.max(0, Math.min(5, n)))}{'☆'.repeat(5 - Math.max(0, Math.min(5, n)))}</span>;
}

function useSEO(settings) {
  useEffect(() => {
    if (!settings) return;
    const title = settings.seo_title || settings.hero_title || 'Vitrine';
    document.title = title;
    const meta = [
      ['description', settings.seo_description || settings.hero_subtitle || ''],
      ['og:title', title],
      ['og:description', settings.seo_description || settings.hero_subtitle || ''],
      ['og:image', settings.seo_image || settings.hero_avatar || ''],
      ['og:type', 'website'],
      ['twitter:card', 'summary_large_image'],
    ];
    const created = [];
    meta.forEach(([key, val]) => {
      if (!val) return;
      const attr = key.startsWith('og:') || key.startsWith('twitter:') ? 'property' : 'name';
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); created.push(el); }
      el.setAttribute('content', val);
    });
    return () => { created.forEach(el => el.remove()); };
  }, [settings]);
}

export default function VitrinePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', context: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    showcaseAPI.getPublic()
      .then(r => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  const settings = data?.settings;
  useSEO(settings);

  const orderedSections = useMemo(() => {
    const secs = settings?.sections?.length ? settings.sections : [];
    return [...secs].filter(s => s.enabled).sort((a, b) => a.order - b.order);
  }, [settings]);

  const scrollToContact = (context = '') => {
    setForm(f => ({ ...f, context, subject: f.subject || (context ? `Demande : ${context}` : '') }));
    document.getElementById('v-contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return;
    setSending(true);
    try { await showcaseAPI.submitContact(form); setSent(true); setForm({ name: '', email: '', subject: '', message: '', context: '' }); }
    catch { alert('Erreur lors de l\'envoi, réessayez.'); }
    finally { setSending(false); }
  };

  if (loading) return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#64748b' }}>Chargement…</div>;
  if (error || !settings) return <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', color: '#64748b' }}>Vitrine indisponible.</div>;

  const tpl = `tpl-${settings.template || 'modern'}`;
  const accentStyle = settings.accent_color ? { '--v-accent': settings.accent_color, '--v-accent-strong': settings.accent_color } : {};
  const sectionTitle = (key, fallback) => orderedSections.find(s => s.key === key)?.title || fallback;
  const social = settings.social || {};
  const socialEntries = Object.entries({ LinkedIn: social.linkedin, GitHub: social.github, Facebook: social.facebook, YouTube: social.youtube, Site: social.website }).filter(([, v]) => v);

  const renderSection = (key) => {
    switch (key) {
      case 'formations': {
        const catalog = data.formations_catalog || [];
        if (!data.formations?.length && !catalog.length) return null;
        return (
          <section className="v-section" id="formations" key={key}>
            <div className="v-wrap">
              <h2 className="v-section-title">{sectionTitle('formations', 'Formations')}</h2>
              <p className="v-section-sub">Programmes et sessions ouvertes aux inscriptions.</p>
              {catalog.length > 0 && (
                <div className="v-grid v-grid-2" style={{ marginBottom: data.formations?.length ? 18 : 0 }}>
                  {catalog.map(f => (
                    <div className="v-card" key={f.id}>
                      {f.cover_image && <img src={f.cover_image} alt="" style={{ width: '100%', height: 150, objectFit: 'cover', borderRadius: 12, marginBottom: 12 }} onError={(e) => (e.target.style.display = 'none')} />}
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {f.level && <span className="v-badge">{f.level}</span>}
                        {f.duration && <span className="v-tag">{f.duration}</span>}
                      </div>
                      <h3 style={{ margin: '10px 0 4px', fontSize: 19, fontWeight: 800 }}>{f.title}</h3>
                      {f.subtitle && <p style={{ color: 'var(--v-muted)', fontSize: 14, margin: '0 0 8px' }}>{f.subtitle}</p>}
                      {f.description && <p style={{ color: 'var(--v-muted)', fontSize: 13, margin: '0 0 12px' }}>{f.description.slice(0, 160)}{f.description.length > 160 ? '…' : ''}</p>}
                      {f.modules?.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>{f.modules.slice(0, 5).map(m => <span key={m} className="v-tag">{m}</span>)}</div>}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                        {f.price_from != null && <span className="v-price">À partir de {f.price_from} {f.currency || 'EUR'}</span>}
                      </div>
                      {f.cta_type === 'contact'
                        ? <button className="v-btn v-btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} onClick={() => scrollToContact(`formation:${f.title}`)}>{f.cta_label || 'Demander le programme'}</button>
                        : <a className="v-btn v-btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 12 }} href={f.cta_url || '#'} target="_blank" rel="noreferrer">{f.cta_label || "S'inscrire"}</a>}
                    </div>
                  ))}
                </div>
              )}
              <div className="v-grid v-grid-2">
                {data.formations.map(f => {
                  const price = f.prices?.[f.primary_currency] ?? Object.values(f.prices || {})[0];
                  return (
                    <div className="v-card" key={f.slug}>
                      {f.course_type_name && <span className="v-badge">{f.course_type_name}</span>}
                      <h3 style={{ margin: '12px 0 6px', fontSize: 19, fontWeight: 800 }}>{f.name}</h3>
                      {f.description && <p style={{ color: 'var(--v-muted)', fontSize: 14, margin: '0 0 12px' }}>{f.description.slice(0, 140)}{f.description.length > 140 ? '…' : ''}</p>}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 8 }}>
                        {price != null && <span className="v-price">{price} {f.primary_currency}</span>}
                        {f.spots_remaining != null && <span style={{ fontSize: 12, color: 'var(--v-muted)' }}>{f.spots_remaining} places restantes</span>}
                      </div>
                      <a className="v-btn v-btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 14 }} href={f.register_url}>S'inscrire</a>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
        );
      }
      case 'projects':
        if (!data.projects?.length) return null;
        return (
          <section className="v-section" id="projets" key={key}>
            <div className="v-wrap">
              <h2 className="v-section-title">{sectionTitle('projects', 'Projets')}</h2>
              <p className="v-section-sub">Réalisations & cas concrets.</p>
              <div className="v-grid v-grid-2">
                {data.projects.map(p => (
                  <div className="v-card" key={p.id}>
                    {p.cover_image && <img src={p.cover_image} alt="" style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 12, marginBottom: 14 }} onError={(e) => (e.target.style.display = 'none')} />}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {p.category && <span className="v-badge">{p.category}</span>}
                      {p.featured && <span className="v-stars">★</span>}
                    </div>
                    <h3 style={{ margin: '10px 0 6px', fontSize: 18, fontWeight: 800 }}>{p.title}</h3>
                    {p.summary && <p style={{ color: 'var(--v-muted)', fontSize: 14, margin: '0 0 12px' }}>{p.summary}</p>}
                    {p.tech_stack?.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>{p.tech_stack.map(t => <span key={t} className="v-tag">{t}</span>)}</div>}
                    <div style={{ display: 'flex', gap: 10 }}>
                      {p.demo_url && <a className="v-btn v-btn-ghost" href={p.demo_url} target="_blank" rel="noreferrer">Démo ↗</a>}
                      {p.repo_url && <a className="v-btn v-btn-ghost" href={p.repo_url} target="_blank" rel="noreferrer">Code ↗</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case 'services':
        if (!data.services?.length) return null;
        return (
          <section className="v-section" id="services" key={key}>
            <div className="v-wrap">
              <h2 className="v-section-title">{sectionTitle('services', 'Services')}</h2>
              <p className="v-section-sub">Prestations sur mesure.</p>
              <div className="v-grid v-grid-3">
                {data.services.map(s => (
                  <div className="v-card" key={s.id} style={{ display: 'flex', flexDirection: 'column' }}>
                    <div style={{ fontSize: 30 }}>{s.icon || '⚙️'}</div>
                    <h3 style={{ margin: '10px 0 6px', fontSize: 17, fontWeight: 800 }}>{s.title}</h3>
                    {s.description && <p style={{ color: 'var(--v-muted)', fontSize: 14, margin: '0 0 12px' }}>{s.description}</p>}
                    {s.features?.length > 0 && <ul style={{ margin: '0 0 14px', padding: 0, listStyle: 'none', fontSize: 13, color: 'var(--v-muted)' }}>{s.features.map(f => <li key={f} style={{ padding: '2px 0' }}>✓ {f}</li>)}</ul>}
                    <div style={{ marginTop: 'auto' }}>
                      {s.price_from != null && <div className="v-price" style={{ marginBottom: 10 }}>À partir de {s.price_from} {s.currency || 'EUR'}</div>}
                      {s.cta_type === 'contact'
                        ? <button className="v-btn v-btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => scrollToContact(`service:${s.title}`)}>{s.cta_label || 'Demander un devis'}</button>
                        : <a className="v-btn v-btn-primary" style={{ width: '100%', justifyContent: 'center' }} href={s.cta_url || '#'} target="_blank" rel="noreferrer">{s.cta_label || (s.cta_type === 'stripe' ? 'Réserver' : 'En savoir plus')}</a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case 'experience':
        if (!data.experience?.length) return null;
        return (
          <section className="v-section" id="parcours" key={key}>
            <div className="v-wrap">
              <h2 className="v-section-title">{sectionTitle('experience', 'Parcours')}</h2>
              <p className="v-section-sub">Expérience, formations & certifications.</p>
              <div className="v-timeline" style={{ maxWidth: 720 }}>
                {data.experience.map((x, i) => (
                  <div className="v-tl-item" key={x.id}>
                    <div className="v-tl-dot" />
                    {i < data.experience.length - 1 && <div className="v-tl-line" />}
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        {x.logo && <img src={x.logo} alt="" style={{ width: 28, height: 28, borderRadius: 6, objectFit: 'cover' }} onError={(e) => (e.target.style.display = 'none')} />}
                        <strong style={{ fontSize: 16 }}>{x.role}</strong>
                        {x.type && x.type !== 'experience' && <span className="v-badge">{x.type === 'certification' ? 'Certification' : 'Formation'}</span>}
                      </div>
                      <div style={{ color: 'var(--v-muted)', fontSize: 13, margin: '3px 0' }}>
                        {[x.company, x.location].filter(Boolean).join(' · ')}
                        {(x.period_start || x.period_end !== undefined) && <> — {x.period_start || ''}{x.period_start ? ' → ' : ''}{x.period_end || "Aujourd'hui"}</>}
                      </div>
                      {x.description && <p style={{ fontSize: 14, margin: '6px 0 0', color: 'var(--v-fg)' }}>{x.description}</p>}
                      {x.tags?.length > 0 && <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>{x.tags.map(t => <span key={t} className="v-tag">{t}</span>)}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      case 'testimonials':
        if (!data.testimonials?.length) return null;
        return (
          <section className="v-section" id="temoignages" key={key}>
            <div className="v-wrap">
              <h2 className="v-section-title">{sectionTitle('testimonials', 'Témoignages')}</h2>
              <div className="v-grid v-grid-3">
                {data.testimonials.map(t => (
                  <div className="v-card" key={t.id}>
                    <Stars n={t.rating || 5} />
                    <p style={{ fontSize: 14, margin: '10px 0 14px', fontStyle: 'italic' }}>“{t.quote}”</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {t.avatar && <img src={t.avatar} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} onError={(e) => (e.target.style.display = 'none')} />}
                      <div>
                        <div style={{ fontWeight: 700, fontSize: 14 }}>{t.author_name}</div>
                        <div style={{ color: 'var(--v-muted)', fontSize: 12 }}>{[t.author_role, t.company].filter(Boolean).join(', ')}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`vitrine-root ${tpl}`} style={accentStyle}>
      <style>{THEME_CSS}</style>

      {/* NAV */}
      <nav className="v-nav">
        <div className="v-nav-inner">
          <strong style={{ fontSize: 16 }}>{settings.hero_title?.split('—')[0]?.trim() || 'LearnTakapes'}</strong>
          <div className="links">
            {orderedSections.map(s => (
              <a key={s.key} className="link" href={`#${{ formations: 'formations', projects: 'projets', services: 'services', experience: 'parcours', testimonials: 'temoignages' }[s.key]}`}>{s.title || s.key}</a>
            ))}
            <a className="link" href="#v-contact">Contact</a>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="v-hero">
        {settings.hero_background && <div className="v-hero-bg" style={{ backgroundImage: `url(${settings.hero_background})` }} />}
        <div className="v-wrap v-hero-inner">
          {settings.hero_avatar && <img className="v-avatar" src={settings.hero_avatar} alt="" onError={(e) => (e.target.style.display = 'none')} />}
          <div>
            {settings.hero_tagline && <span className="v-badge" style={{ marginBottom: 12 }}>{settings.hero_tagline}</span>}
            <h1>{settings.hero_title || 'Ma vitrine'}</h1>
            {settings.hero_subtitle && <p className="v-sub">{settings.hero_subtitle}</p>}
            <div style={{ display: 'flex', gap: 12, marginTop: 18, flexWrap: 'wrap' }}>
              {settings.hero_cta_label && <a className="v-btn v-btn-primary" href={settings.hero_cta_url || '#formations'}>{settings.hero_cta_label}</a>}
              <button className="v-btn v-btn-ghost" onClick={() => scrollToContact('')}>Me contacter</button>
            </div>
          </div>
        </div>
      </header>

      {/* SECTIONS */}
      {orderedSections.map(s => renderSection(s.key))}

      {/* CONTACT */}
      <section className="v-section" id="v-contact">
        <div className="v-wrap" style={{ maxWidth: 620 }}>
          <h2 className="v-section-title">Contact</h2>
          <p className="v-section-sub">Une question, un projet, un devis ? Écris-moi.</p>
          {sent ? (
            <div className="v-card" style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 34 }}>✅</div>
              <p style={{ fontWeight: 700, marginTop: 8 }}>Message envoyé, merci !</p>
              <p style={{ color: 'var(--v-muted)', fontSize: 14 }}>Je te recontacte au plus vite.</p>
            </div>
          ) : (
            <form className="v-card" onSubmit={submit} style={{ display: 'grid', gap: 12 }}>
              {form.context && <div className="v-badge" style={{ justifySelf: 'start' }}>{form.context}</div>}
              <input className="v-input" placeholder="Nom *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              <input className="v-input" type="email" placeholder="Email *" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <input className="v-input" placeholder="Sujet" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} />
              <textarea className="v-input" rows={5} placeholder="Message *" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required />
              <button className="v-btn v-btn-primary" style={{ justifyContent: 'center' }} type="submit" disabled={sending}>{sending ? 'Envoi…' : 'Envoyer'}</button>
            </form>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="v-footer">
        <div className="v-wrap" style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <strong>{settings.hero_title?.split('—')[0]?.trim() || 'LearnTakapes'}</strong>
            <div style={{ marginTop: 4 }}>{[settings.contact_email, settings.contact_phone, settings.location].filter(Boolean).join(' · ')}</div>
          </div>
          {socialEntries.length > 0 && (
            <div className="v-socials">
              {socialEntries.map(([label, url]) => <a key={label} href={url} target="_blank" rel="noreferrer" style={{ color: 'var(--v-accent-strong)', fontWeight: 600 }}>{label} ↗</a>)}
            </div>
          )}
        </div>
        <div className="v-wrap" style={{ marginTop: 18, fontSize: 12, opacity: 0.7 }}>Propulsé par LearnTakapes · © {new Date().getFullYear()}</div>
      </footer>
    </div>
  );
}
